<script setup>
import { ref } from 'vue';
import XlButton from '../xl/XlButton.vue';
import { useUnsavedChanges } from '../../composables/useUnsavedChanges';

const props = defineProps({
  backLabel: { type: String, required: true },
  title: { type: String, required: true },
  saveLabel: { type: String, default: '' },
  saveDisabled: { type: Boolean, default: false },
  testid: { type: String, default: 'comms-editor' },
  /**
   * What the editor holds, for the unsaved-changes prompt: compared with what
   * it held when the person first touched it. Leaving with it changed — by the
   * sidebar, to another section or back to this one — asks first.
   */
  changes: { type: null, default: undefined },
});

const emit = defineEmits(['back', 'cancel', 'save']);

const root = ref(null);
// Each of these editors opens on its tab's page, not a route of its own, so
// the sidebar's own section closes it back to the list (onSectionHome).
useUnsavedChanges({
  key: 'comms-editor',
  snapshot: () => props.changes,
  root,
  onSectionHome: () => emit('back'),
});

// Guarded here as well as on the button: the testid lands on the XlButton
// wrapper, so a click reaches this handler even when the inner button is
// disabled.
const save = () => {
  if (props.saveDisabled) return;
  emit('save');
};
</script>

<template>
  <div ref="root" class="comms-editor-page" :data-testid="props.testid">
    <XlButton
      variant="ghost"
      size="sm"
      class="comms-editor-page__back"
      :data-testid="`${props.testid}-back`"
      @click="emit('back')"
    >
      <template #icon>
        <svg width="15" height="15" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="M12.5 4.5L7 10l5.5 5.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </template>
      {{ props.backLabel }}
    </XlButton>

    <h2 class="comms-editor-page__title">{{ props.title }}</h2>

    <div v-if="$slots.meta" class="comms-editor-page__meta"><slot name="meta" /></div>

    <!--
      Two columns, because every one of these editors is a definition with a
      consequence: the form on the left, and what it will actually do on the
      right. Collapses to one column below the breakpoint, aside last.
    -->
    <div class="comms-editor-page__grid" :class="{ 'comms-editor-page__grid--solo': !$slots.aside }">
      <!--
        Floating field labels (pge-input-text/pge-dropdown/pge-textarea/pge-multiselect)
        mask their own border with an inherited background-color, so every
        container between here and the field must stay opaque or the border
        draws straight through the label text.
      -->
      <!-- The buttons follow the form, not whichever column is taller. -->
      <div class="comms-editor-page__main">
        <div class="comms-editor-page__form"><slot /></div>

        <div v-if="props.saveLabel" class="comms-editor-page__actions">
          <XlButton
            :data-testid="`${props.testid}-save`"
            :disabled="props.saveDisabled"
            @click="save"
          >{{ props.saveLabel }}</XlButton>
          <XlButton
            :data-testid="`${props.testid}-cancel`"
            variant="ghost"
            @click="emit('cancel')"
          >Cancel</XlButton>
        </div>
      </div>

      <aside v-if="$slots.aside" class="comms-editor-page__aside"><slot name="aside" /></aside>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.comms-editor-page {
  &__back { margin-bottom: 18px; }

  &__title {
    margin: 0 0 20px;
    font-size: 24px;
    font-weight: var(--xl-font-weight-bold);
    letter-spacing: -0.03em;
    color: var(--xl-color-primary-text);
  }

  &__meta {
    margin: -12px 0 20px;
    font-size: var(--xl-type-body-small-size);
    color: var(--xl-color-outline);
  }

  &__grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    align-items: start;
    gap: 12px;
  }

  // Alone, the form keeps a column's width rather than stretching the page:
  // a 60-character name field a thousand pixels wide reads as a broken layout.
  &__grid--solo { grid-template-columns: minmax(0, 620px); }

  @media (max-width: 900px) {
    &__grid, &__grid--solo { grid-template-columns: minmax(0, 1fr); }
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 22px 24px;
    border: 1px solid var(--xl-color-border);
    border-radius: var(--xl-radius-lg);
    background-color: #fff;
    box-shadow: var(--xl-shadow-1);
    margin-bottom: 14px;
  }

  &__aside {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__main { min-width: 0; }

  &__actions {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 4px;
  }
}
</style>
